import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property, state } from 'lit/decorators.js';

import { RTL_LANGUAGE, CUSTOM_FONT_URLS, EFFECTIVE_KEY, FONT_BASE_URL } from '../utils/constant';
import cookieConfigManager, { isEffectiveCountry } from '../utils/cookie-consent-manager';
import { readUserConfig } from '../utils/config';
import { monitorSelfErrors, teaReport } from '$tea';
import { showToast, loadCustomFonts } from '../utils/ui';
import { NavigationInfos, CookieConsent, DisabledAccessKeysConfig } from '../interface/cookie-banner';
import styles from './cookie-banner.scss';
import './setting-modal';
import consentMode from '../utils/consent-mode';
import { initConsentSDK } from '../utils/consent';
import { monitorModule } from '../utils/slardar/slardar';
import { CUJStatus } from '@pns-fe/sdk-common-modules';

CUSTOM_FONT_URLS.forEach(([fontName, fontPath]) => {
  let baseUrl = FONT_BASE_URL.ROW;
  if (process.env.AWEME_REGION && FONT_BASE_URL[process.env.AWEME_REGION]) {
    baseUrl = FONT_BASE_URL[process.env.AWEME_REGION];
  }

  const fontUrl = `${baseUrl}${fontPath}`;
  loadCustomFonts(fontName, fontUrl);
});

monitorSelfErrors();

@customElement('tiktok-cookie-banner')
export class CookieBanner extends LitElement {
  static override styles = unsafeCSS(styles);

  @property()
  locale!: string;
  @property({
    attribute: 'user-config-ele-id',
  })
  userConfigEleId!: string;
  @property({
    converter: (value: string | null) => 'true' === value,
  })
  disabled = false;
  @property({
    attribute: 'disabled-access-key',
  })
  disabledAccessKey = '';

  @state() i18n: Record<string, string> = {};
  @state() userConfig?: NavigationInfos;
  @state() cookieConsent?: CookieConsent;
  @state() disabledByPolicy = false;
  @state() settingVisible = false;
  @state() settingModalCloseDisabled?: boolean;
  @state() forceUpdater = 1;
  @state() disabledAccessKeyConfig: DisabledAccessKeysConfig = [];
  @state() customTheme = '';
  @state() tenantId = '';

  private region = '';
  private isFTC = false;
  private previousShow = false;
  private isEffective = false;

  get showCookieBanner(): boolean {
    const disabledByAccessKey = this._disabledByAccessKey();
    const show =
      !disabledByAccessKey &&
      !this.disabledByPolicy &&
      cookieConfigManager.isShowCookieBanner({
        isEffective: this.isEffective,
        isFTC: this.isFTC,
      });
    if (show !== this.previousShow) {
      if (show) {
        teaReport('cookie_banner_show');
      }
      this._sendEvent('bannerEvents', {
        type: show ? 'show' : 'hide',
      });
      this.previousShow = show;
    }
    return show;
  }

  /**
   * used by cookie policy page only
   */
  async reload(): Promise<void> {
    this.requestUpdate();
  }

  override connectedCallback(): void {
    super.connectedCallback();

    try {
      const userConfig = readUserConfig(this.userConfigEleId, this.locale);
      if (!userConfig) {
        return;
      }

      teaReport('cookie_banner_load');
      const {
        i18n,
        region,
        isFTC,
        disabled: disabledByPolicy,
        isEffective,
        consent,
        disabledAccessKeys: disabledAccessKeyConfig,
        consentApiDomain,
        customTheme,
        idcRegion,
        disableTea,
        tenantId,
        tea,
      } = userConfig;
      initConsentSDK({ config: consent, region, consentApiDomain, idcRegion, disableTea, tea, tenantId });

      Object.assign(this, {
        i18n,
        region,
        isFTC,
        disabledByPolicy,
        isEffective,
        disabledAccessKeyConfig,
        customTheme,
        tenantId,
      });
      const cookieConsent = cookieConfigManager.getCookieConsents({ isEffective, isFTC });
      consentMode.initializeWithConsent(cookieConsent);
      this._execConsentsGetCallback(cookieConsent);
      this.cookieConsent = cookieConsent;
      this._updatePumbaaContext(isEffective, isFTC);
      this._updateLocalStorage(isEffective, isFTC);
    } catch (e) {
      console.error(e);
    }
  }

  override updated(changedProperties: Map<string, unknown>): void {
    if (changedProperties.has('locale')) {
      const userConfig = readUserConfig(this.userConfigEleId, this.locale);
      if (userConfig?.i18n) {
        this.i18n = userConfig.i18n;
      }
    }
  }

  override firstUpdated(_changedProperties: Map<string, unknown>): void {
    this._sendEvent('bannerEvents', {
      type: 'renderCompleted',
    });
    this._sendEvent('customBannerEvents', {
      eventName: 'onRenderCompleted',
      payload: { showCookieBanner: this.showCookieBanner },
    });
  }

  private _disabledByAccessKey(): boolean {
    try {
      if (!this.disabled || !this.disabledAccessKey || !this.disabledAccessKeyConfig?.length) {
        return false;
      }

      return this.disabledAccessKeyConfig.some((config) => {
        const { path, accessKeys } = config;
        const pathname = location.pathname;
        const reg = new RegExp(path);
        return reg.test(pathname) && (accessKeys.includes(this.disabledAccessKey) || accessKeys.includes('*'));
      });
    } catch (err) {
      console.error(`disabled by access key error, err=${err}`);
      return false;
    }
  }

  private _updatePumbaaContext(isEffective: boolean, isFTC: boolean): void {
    try {
      const cb_enabled = isEffectiveCountry(isEffective || isFTC);
      let metaTag = document.head.querySelector('meta[name="pumbaa-ctx"]') as HTMLMetaElement;
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.name = 'pumbaa-ctx';
        document.head.appendChild(metaTag);
      }
      metaTag.content = `${metaTag.content ? metaTag.content + ',' : ''}cb_enabled=${cb_enabled}`;

      if (window.__PNS_RUNTIME__?.updateContext) {
        window.__PNS_RUNTIME__.updateContext?.({ cb_enabled });
      }
    } catch (err) {
      console.error('update pumbaa context error');
      console.error(err);
    }
  }

  private _updateLocalStorage(isEffective: boolean, isFTC: boolean): void {
    try {
      const isLocalEffective = isEffectiveCountry(isEffective || isFTC);
      localStorage.setItem(EFFECTIVE_KEY, isLocalEffective);
    } catch (err) {
      console.error('update local is effective error');
      console.error(err);
      this._updateWindow(isEffective, isFTC);
    }
  }

  private _updateWindow(isEffective: boolean, isFTC: boolean): void {
    try {
      const isLocalEffective = isEffectiveCountry(isEffective || isFTC);
      window[EFFECTIVE_KEY] = isLocalEffective;
    } catch (err) {
      console.error('window - update local is effective error');
      console.error(err);
    }
  }

  private _execConsentsGetCallback(cookieConsent: CookieConsent): void {
    window.gtm_permission = cookieConsent;
    const consentOptions = {
      region: this.region,
      isFTC: this.isFTC,
      isEffective: this.isEffective,
    };
    this._sendEvent('bannerEvents', {
      type: 'consentGet',
      payload: [cookieConsent, consentOptions],
    });
    this._sendEvent('getConsent', { payload: { cookieConsent, consentOptions } });
  }

  private _handleDescClick(ev: Event): void {
    const { target } = ev;
    if (target instanceof HTMLAnchorElement) {
      const { href } = target;
      if (!href) {
        return;
      }
      if (href.includes('policy')) {
        teaReport('cookie_banner_go_to_policy');
      } else if (href.includes('cookie-settings')) {
        // use settings modal instead
        ev.preventDefault();
        this.openSettingModal();
      }
    }
  }

  private _handleConfirm(): void {
    try {
      monitorModule?.reportCUJ('cookie_banner_accept', CUJStatus.START);

      const { i18n } = this;
      const { cookieBannerSaveToast } = i18n;
      const cookieConsent = cookieConfigManager.setFullConsents();
      consentMode.updateWithConsent(cookieConsent);
      this._execConsentsGetCallback(cookieConsent);
      this._sendEvent('bannerEvents', {
        type: 'accept',
      });
      this.cookieConsent = cookieConsent;
      showToast(cookieBannerSaveToast);
      teaReport('cookie_banner_accept');

      monitorModule?.reportCUJ('cookie_banner_accept', CUJStatus.FINISH);
    } catch (err) {
      monitorModule?.reportCUJ('cookie_banner_accept', CUJStatus.FAILURE);
    }
  }

  private _handleDecline(): void {
    try {
      monitorModule?.reportCUJ('cookie_banner_decline', CUJStatus.START);

      const { i18n } = this;
      const { cookieBannerSaveToast } = i18n;
      const cookieConsent = cookieConfigManager.setEmptyConsents();
      consentMode.updateWithConsent(cookieConsent);
      this._execConsentsGetCallback(cookieConsent);
      this._sendEvent('bannerEvents', {
        type: 'decline',
      });
      this.cookieConsent = cookieConsent;
      showToast(cookieBannerSaveToast);
      teaReport('cookie_banner_decline');

      monitorModule?.reportCUJ('cookie_banner_decline', CUJStatus.FINISH);
    } catch (err) {
      monitorModule?.reportCUJ('cookie_banner_decline', CUJStatus.FAILURE);
    }
  }

  private _handleSave(ev: CustomEvent<CookieConsent>): void {
    const { detail } = ev;
    const { i18n } = this;
    const { cookieBannerSaveToast } = i18n;
    consentMode.updateWithConsent(detail);
    this._execConsentsGetCallback(detail);
    this._sendEvent('bannerEvents', {
      type: 'save',
      payload: [detail],
    });
    this.cookieConsent = detail;
    showToast(cookieBannerSaveToast);
  }

  private _sendEvent<T>(eventName: string, detail: T): void {
    this.dispatchEvent(
      new CustomEvent<T>(eventName, {
        detail,
      }),
    );
  }

  public openSettingModal(modalCloseDisabled?: boolean) {
    this.settingVisible = true;
    this.settingModalCloseDisabled = modalCloseDisabled;
    teaReport('cookie_banner_go_to_settings');
  }

  override render() {
    const { locale = 'en', i18n, tenantId } = this;
    const {
      cookieBannerTitle = '',
      cookieBannerSubTitle = '',
      cookieBannerBtnDecline = '',
      cookieBannerBtnAccept = '',
      cookieBannerBtnSetting = '',
    } = i18n;

    const cookieBanner = this.showCookieBanner
      ? html`
          <div
            class=${classMap({
              'tiktok-cookie-banner': true,
              'is-rtl': RTL_LANGUAGE.includes(locale),
              ...(tenantId ? { [tenantId]: true } : {}),
            })}
          >
            <div class="cookie-content">
              <p class="cookie-title">${cookieBannerTitle}</p>
              <p class="cookie-desc" @click=${this._handleDescClick} .innerHTML=${cookieBannerSubTitle}></p>
            </div>
            <div
              class=${classMap({
                'button-wrapper': true,
                'special-button-wrapper': cookieBannerBtnDecline?.length > 30 || cookieBannerBtnAccept?.length > 30,
              })}
            >
              ${cookieBannerBtnSetting
                ? html`<button type="button" @click=${this.openSettingModal}>${cookieBannerBtnSetting}</button>`
                : nothing}
              <button type="button" @click=${this._handleDecline}>${cookieBannerBtnDecline}</button>
              <button type="button" @click=${this._handleConfirm}>${cookieBannerBtnAccept}</button>
            </div>
          </div>
        `
      : nothing;
    return html`
      <style>
        :host {
          ${this.customTheme}
        }
      </style>
      ${cookieBanner}
      <cookie-setting-modal
        .visible=${this.settingVisible}
        .settingModalCloseDisabled=${this.settingModalCloseDisabled}
        .userConfigEleId=${this.userConfigEleId}
        .locale=${this.locale}
        @close=${() => {
          if (!this.settingModalCloseDisabled) {
            this.settingVisible = false;
          }
        }}
        @save=${this._handleSave}
      >
      </cookie-setting-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cookie-banner': CookieBanner;
  }
}
